import { Button, Empty, Grid, Table, Tooltip, Typography, Row } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  fetchData,
  fixedTableScrollY,
  handleRowDelete,
  Notify,
} from "../../utils/constants";
import CompanyModal from "../Modals/CompanyModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Company, ConfirmAction } from "../../utils/types";
import DeleteConfirmationModal from "../Modals/DeleteConfirmationModal";
import { useUser } from "../../context/UserContext";

const Companies = () => {
  const { authUser } = useUser();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState<ConfirmAction | null>(
    null,
  );

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/companies/getAllCompanies`,
        setCompanies,
      );
    } catch (error) {
      console.error("Failed to fetch companies data:", error);
      Notify("error", "Could not load companies data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteCompany = useCallback(
    (company: Company) => {
      setSelectedCompany(company);
      setIsDeleteModalOpen(true);
      setOnConfirmDelete(
        () => () =>
          handleRowDelete(
            "Company",
            `${process.env.REACT_APP_API_URL}/companies/deleteCompany`,
            company,
            fetchCompanies,
          ),
      );
    },
    [fetchCompanies],
  );

  useEffect(() => {
    if (!authUser) return;
    fetchCompanies();
  }, [authUser, fetchCompanies]);

  const columns = useMemo<ColumnsType<Company>>(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        fixed: isDesktop ? "right" : undefined,
        align: "center",
        render: (_: unknown, record: Company) => (
          <Row>
            <Tooltip title="Edit company">
              <Button
                className="mr-2"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedCompany(record);
                  setIsModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Delete company">
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteCompany(record)}
              />
            </Tooltip>
          </Row>
        ),
      },
    ],
    [handleDeleteCompany, isDesktop],
  );

  return (
    <div className="section-container">
      <Typography.Title level={4}>Companies</Typography.Title>
      <CompanyModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        company={selectedCompany}
        refreshData={fetchCompanies}
      />
      {onConfirmDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            onConfirmDelete();
            setIsDeleteModalOpen(false);
          }}
        />
      )}
      <Button
        title="Add company"
        className="new-company-btn"
        onClick={() => {
          setSelectedCompany(null);
          setIsModalOpen(true);
        }}
        type="primary"
        icon={<PlusOutlined />}
      >
        Add company
      </Button>
      <Table
        className="view-table"
        scroll={{ x: true, y: fixedTableScrollY }}
        columns={columns}
        dataSource={companies}
        rowKey={(record) => `${record.id}-${record.name}`}
        pagination={false}
        loading={{ spinning: loading, tip: "Loading companies..." }}
        locale={{
          emptyText: <Empty description="No companies found." />,
        }}
      />
    </div>
  );
};

export default Companies;
